import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useDropzone } from "react-dropzone";
import { useDispatch, useSelector } from "react-redux";

import * as api from "../../../api/index";
import { uploadQuote } from "../../../actions/upload";

import {
  ArrowDropDown,
  Check,
  Close,
  Delete,
  EventOutlined,
  PublishOutlined,
  Search,
} from "@material-ui/icons";

import Languages from "../../../data/languages";
import Subjects from "../../../data/subjects";

import "./Quote.css";

const Quote = ({ setShowPrice, showPrice }) => {
  const dispatch = useDispatch();
  const reloadQuote = useSelector((state) => state.upload);

  const [files, setFiles] = useState([]);
  const [urgent, setUrgent] = useState(false);

  const [selectedFile, setSelectedFile] = useState({});
  const [counts, setCounts] = useState(0);

  const [regexFrom, setRegexFrom] = useState(".");
  const [regexTo, setRegexTo] = useState(".");
  const [multiRegexTo, setMultiRegexTo] = useState(".");
  const [regexSubject, setRegexSubject] = useState(".");
  const [showError, setShowError] = useState(false);
  const [errMsg, setErrMsg] = useState("");
  const [totalSizes, setTotalSizes] = useState(0);
  const uncountedFiles = files.filter((file) => file.count === 0).length;
  const disable =
    files.length < 1 ||
    reloadQuote.totalCount === undefined ||
    (reloadQuote.totalCount === 0 && uncountedFiles === 0);

  const newRegexFrom = new RegExp(regexFrom, "i");
  const newRegexTo = new RegExp(regexTo, "i");
  const newRegexMultiTo = new RegExp(multiRegexTo, "i");
  const newSubject = new RegExp(regexSubject, "i");

  const languages = Languages.map((lang) => {
    return lang.name.split(/[,|;]/)[0];
  }).sort();

  const languagesFrom = languages.filter((lang) => newRegexFrom.test(lang));
  const languagesTo = languages.filter((lang) => newRegexTo.test(lang));
  const MultiLanguagesTo = languages.filter((lang) =>
    newRegexMultiTo.test(lang)
  );
  const subjects = Subjects.filter((subject) =>
    newSubject.test(subject.subject)
  );

  const closeUpload = () =>
    document.querySelector("#quote__details__3").removeAttribute("open");
  const closeLanguages = () =>
    document.querySelector(".quote__more__langs").removeAttribute("open");

  const updateUpload = useCallback(
    (obj) => {
      dispatch(uploadQuote(obj));
    },
    [dispatch]
  );

  const handleRemoveFile = async (index) => {
    const fileCount = files.filter((name, i) => i === index)[0].count;
    setFiles((files) => files.filter((name, i) => i !== index));

    setCounts((counts) => counts - fileCount);
  };

  const handleDropdownSelect = (value, type) => {
    if (type === "from") {
      updateUpload({ languageFrom: value });
      document.querySelector("#quote__details__1").removeAttribute("open");
    }
    if (type === "to") {
      updateUpload({ languageTo: [value] });
      document.querySelector("#quote__details__2").removeAttribute("open");
    }
    if (type === "subject") {
      updateUpload({ subject: value });
      document.querySelector("#quote__details__4").removeAttribute("open");
    }
    if (type === "date") {
      document.querySelector("#quote__details__5").removeAttribute("open");
    }
    if (type === "multiple") {
      if (reloadQuote.languageTo.indexOf(value) !== -1) {
        if (reloadQuote.languageTo.length === 1) return false;
        return updateUpload({
          languageTo: reloadQuote.languageTo.filter(
            (language) => language !== value
          ),
        });
      }
      updateUpload({ languageTo: [...reloadQuote.languageTo, value] });
    }
  };

  const handleWordCount = useCallback(async () => {
    const getIndex = (name) => {
      let index;
      files.map((file, i) => {
        if (file.name === name) index = i;
        return file;
      });

      return index;
    };

    const toDataURL = async (file, count, index) => {
      const reader = new FileReader();

      reader.readAsDataURL(file);

      await reader.addEventListener("load", () => {
        if (files[index]) {
          files[index] = {
            count,
            name: file.name,
            type: file.type,
            size: file.size,
            base64: reader.result,
          };
        }
      });

      setCounts(
        files.map((file) => file.count).reduce((acc, curr) => acc + curr, 0)
      );
      setTotalSizes(
        files.map((file) => file.size).reduce((acc, curr) => acc + curr, 0)
      );
    };

    try {
      const formData = new FormData();
      formData.append("file", selectedFile);
      const {
        data: { count, name },
      } = await api.getWordCount(formData);
      const adjustCount = count < 6 ? 0 : count;
      const index = getIndex(name);
      if (files[index]) files[index].count = adjustCount;

      toDataURL(selectedFile, adjustCount, index);
    } catch (e) {
      console.log(e.message);
    }
  }, [selectedFile, files]);

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop: (acceptedFiles, fileRejections) => {
      setFiles((files) => files.concat(acceptedFiles));
      fileRejections.forEach((file) => {
        file.errors.forEach((err) => {
          if (err.code === "too-many-files") {
            setErrMsg("Upload one file at a time");
            setShowError(true);
            setTimeout(() => {
              setShowError(false);
            }, 7000);
          }
          if (err.code === "file-too-large") {
            setErrMsg("Total files size exceed 12MB");
            setShowError(true);
            setTimeout(() => {
              setShowError(false);
            }, 7000);
          }
        });
      });
    },
    maxFiles: 1,
    maxSize: 12000000 - totalSizes,
  });

  const handleShowPrices = (e) => {
    e.preventDefault();
    setShowPrice(!showPrice);
  };

  useEffect(() => {
    if (Object.keys(selectedFile).length > 0) {
      handleWordCount();
    }
    updateUpload({ files, totalCount: counts });
  }, [files, selectedFile, counts, handleWordCount, updateUpload]);

  useEffect(() => {
    if (uncountedFiles === 0) setShowError(false);
    if (uncountedFiles > 0) {
      setErrMsg(
        `File can't be read correctly, continue for manual verification`
      );
      setShowError(true);
    }
  }, [uncountedFiles, totalSizes]);

  useMemo(() => {
    if (reloadQuote.totalCount) {
      setCounts(reloadQuote.totalCount);
    }
    if (reloadQuote.files) {
      setFiles(reloadQuote.files);
    }
    if (reloadQuote.urgent) {
      setUrgent(reloadQuote.urgent);
    }
  }, [reloadQuote]);

  useEffect(() => {
    const uploadModal = document.querySelector(".quote__file__ctn");
    const moreLanguages = document.querySelector(".quote__langs__ctn");

    const options = {
      threshold: 0,
      rootMargin: "0px",
    };

    const observerUpload = new IntersectionObserver(
      (entries, observerUpload) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            document.getElementsByTagName("BODY")[0].style.overflow = "hidden";
          } else {
            document.getElementsByTagName("BODY")[0].style.overflow = "auto";
          }
        });
      },
      options
    );
    observerUpload.observe(uploadModal);
    observerUpload.observe(moreLanguages);
  }, []);

  const optionStyle = {
    backgroundColor: "#ff5e15",
    color: "white",
  };

  return (
    <form
      className="quote box-shadow-sm p-40"
      onSubmit={(e) => e.preventDefault()}
    >
      <div className="quote__top grid-3">
        <label>
          <p className="font-bold font-big mb-10 ml-10">From</p>
          <details id="quote__details__1" className="position-relative">
            <summary className="input d-flex align-center justify-between pointer list-none">
              {reloadQuote.languageFrom} <ArrowDropDown />
            </summary>
            <div className="quote__drop__down d-flex flex-column position-absolute w-100 z-4">
              <div className="d-flex align-center border-bottom-1">
                <input
                  className="border-0 outline-0 w-100 font-normal p-20"
                  type="text"
                  placeholder="Search..."
                  onChange={(e) => setRegexFrom(e.target.value)}
                />
                <Search className="icon-light-grey m-20" />
              </div>
              <div className="d-flex flex-column overflow-auto">
                {languagesFrom.length < 1 ? (
                  <span className="p-20">Not Found</span>
                ) : (
                  languagesFrom.map((language) => (
                    <span
                      className="quote__drop__lang d-flex justify-between p-20 border-bottom-1 text-capitalise pointer"
                      key={language}
                      style={{
                        color:
                          language === reloadQuote.languageFrom
                            ? "#ff5e15"
                            : "",
                      }}
                      onClick={() => handleDropdownSelect(language, "from")}
                    >
                      {language}
                      {language === reloadQuote.languageFrom && <Check />}
                    </span>
                  ))
                )}
              </div>
            </div>
          </details>
        </label>
        <label>
          <div className="d-flex align-center justify-between mb-10 mx-10">
            <p className="font-bold font-big">To</p>
            <details className="quote__more__langs">
              <summary className="font-sm font-heavy pointer list-none">
                Select multiple languages
              </summary>
              <div className="quote__langs__ctn mx-auto z-5">
                <div>
                  <div className="d-flex align-center justify-center px-20">
                    <Search className="icon-light-grey" />
                    <input
                      className="font-heavy font-normal border-0 outline-0 w-100 p-20"
                      type="text"
                      placeholder="Search ..."
                      onChange={(e) => setMultiRegexTo(e.target.value)}
                    />
                    <Close className="pointer" onClick={closeLanguages} />
                  </div>
                  <div className="p-20 border-top-1 border-bottom-1">
                    {reloadQuote.languageTo.map((language) => (
                      <span key={language} className="font-heavy p-10">
                        {language}
                      </span>
                    ))}
                  </div>
                </div>
                <div className="quote__langs__grid p-20 overflow-auto">
                  {MultiLanguagesTo.length < 1 ? (
                    <span
                      style={{ minWidth: "200px" }}
                      className="font-heavy font-big p-20"
                    >
                      Not Found
                    </span>
                  ) : (
                    MultiLanguagesTo.map((language) => (
                      <span
                        className="quote__drop__lang text-center p-20 text-capitalise pointer"
                        key={language}
                        style={
                          reloadQuote.languageTo.indexOf(language) !== -1
                            ? optionStyle
                            : {}
                        }
                        onClick={() =>
                          handleDropdownSelect(language, "multiple")
                        }
                      >
                        {language}
                      </span>
                    ))
                  )}
                </div>
                <div className="quote__more__langs__bottom d-flex align-center justify-between border-top-1 p-20">
                  <span className="font-heavy">
                    {reloadQuote.languageTo.length} language
                    {reloadQuote.languageTo.length > 1 ? "s" : ""} selected
                  </span>
                  <div className="quote__more__langs__buttons">
                    <button
                      className="font-normal font-heavy border-0 outline-0 py-10 px-20 mr-20 rounded"
                      onClick={() => updateUpload({ languageTo: ["Spanish"] })}
                    >
                      Reset
                    </button>
                    <button
                      className="font-normal font-heavy border-0 outline-0 py-10 px-20 rounded"
                      onClick={closeLanguages}
                    >
                      Done
                    </button>
                  </div>
                </div>
              </div>
            </details>
          </div>
          <details id="quote__details__2" className="position-relative">
            <summary className="input d-flex align-center justify-between pointer list-none">
              <span className="quote__lang__to ellipsis">
                {reloadQuote.languageTo.map((language, i) =>
                  reloadQuote.languageTo.length === 1 ||
                  reloadQuote.languageTo.length - 1 === i
                    ? language
                    : `${language}, `
                )}
              </span>
              <ArrowDropDown />
            </summary>
            <div className="quote__drop__down d-flex flex-column position-absolute w-100 z-4">
              <div className="d-flex align-center border-bottom-1">
                <input
                  className="border-0 outline-0 w-100 font-normal p-20"
                  type="text"
                  placeholder="Search..."
                  onChange={(e) => setRegexTo(e.target.value)}
                />
                <Search className="icon-light-grey m-20" />
              </div>
              <div className="d-flex flex-column overflow-auto">
                {languagesTo.length < 1 ? (
                  <span className="p-20">Not Found</span>
                ) : (
                  languagesTo.map((language) => (
                    <span
                      className="quote__drop__lang d-flex justify-between p-20 border-bottom-1 text-capitalise pointer"
                      key={language}
                      style={{
                        color:
                          reloadQuote.languageTo.indexOf(language) !== -1
                            ? "#ff5e15"
                            : "",
                      }}
                      onClick={() => handleDropdownSelect(language, "to")}
                    >
                      {language}
                      {reloadQuote.languageTo.indexOf(language) !== -1 && (
                        <Check />
                      )}
                    </span>
                  ))
                )}
              </div>
            </div>
          </details>
        </label>
        <label>
          <p className="font-bold font-big mb-10 ml-10">Subject</p>
          <details id="quote__details__4" className="position-relative">
            <summary className="input d-flex align-center justify-between pointer list-none">
              {reloadQuote.subject} <ArrowDropDown />
            </summary>
            <div className="quote__drop__down d-flex flex-column position-absolute w-100 z-4">
              <div className="d-flex align-center border-bottom-1">
                <input
                  className="border-0 outline-0 w-100 font-normal p-20"
                  type="text"
                  placeholder="Search..."
                  onChange={(e) => setRegexSubject(e.target.value)}
                />
                <Search className="icon-light-grey m-20" />
              </div>
              <div className="d-flex flex-column overflow-auto">
                {subjects.length < 1 ? (
                  <span className="p-20">Not Found</span>
                ) : (
                  subjects.map((subject) => (
                    <span
                      className="quote__drop__lang d-flex justify-between p-20 border-bottom-1 text-capitalise pointer"
                      key={subject.subject}
                      style={{
                        color:
                          subject.subject === reloadQuote.subject
                            ? "#ff5e15"
                            : "",
                      }}
                      onClick={() =>
                        handleDropdownSelect(subject.subject, "subject")
                      }
                    >
                      {subject.subject}
                      {subject.subject === reloadQuote.subject && <Check />}
                    </span>
                  ))
                )}
              </div>
            </div>
          </details>
        </label>
      </div>
      <div className="quote__bottom grid-3 mt-40">
        <div>
          <p className="font-bold font-big mb-10 ml-10">Upload</p>
          <div className="quote__count input">
            <span className="font-heavy font-normal border-right-2 w-50">
              {uncountedFiles > 0
                ? "NA"
                : counts > 0
                ? `${counts} words`
                : "0 word"}
            </span>
            <div className="d-flex justify-end w-50">
              <details id="quote__details__3">
                <summary className="list-none border-1 rounded outline-0 font-heavy font-normal px-10 pointer">
                  <span className="quote__1150h">Upload files</span>
                  <span className="quote__1150v">
                    <PublishOutlined className="rotate-180 icon-light-grey" />
                  </span>
                </summary>
                <div className="quote__file__ctn d-flex flex-column mx-auto rounded z-5">
                  <div className="d-flex align-center justify-between py-10 px-20 border-bottom-1">
                    <span>Upload document</span>
                    <button
                      className="quote__file__top__btn border-0 outline-0 p-10 cursor bg-transparent"
                      onClick={closeUpload}
                    >
                      <Close />
                    </button>
                  </div>
                  <div
                    className="quote__error__info text-center font-bolder overflow-hidden"
                    style={{
                      height: showError ? "40px" : "",
                      opacity: showError ? "1" : "",
                    }}
                  >
                    {errMsg}
                  </div>
                  <div
                    className="quote__file__body m-20 p-20 overflow-auto position-relative"
                    style={{
                      justifyContent:
                        files.length > 0 ? "flex-start" : "center",
                      borderStyle: isDragActive ? "solid" : "dotted",
                    }}
                  >
                    {files.length > 0 && (
                      <div className="w-100 mb-20 z-3">
                        {files.map((file, index) => (
                          <div
                            className="quote__uploads d-flex align-center justify-between p-5 border-1 w-100 mb-10"
                            key={file.name.concat(index)}
                          >
                            <span className="font-heavy font-normal ml-10">
                              {file.name}
                            </span>
                            <div className="d-flex align-center h-100">
                              {file.count >= 0 ? (
                                <small>
                                  {file.count}
                                  {file.count < 1 ? " word" : " words"}
                                </small>
                              ) : (
                                <span className="spin"></span>
                              )}
                              <button
                                className="d-flex justify-center align-center p-5 bg-transparent border-0 outline-0 ml-5"
                                onClick={() => handleRemoveFile(index)}
                              >
                                <Delete className="icon-light-grey" />
                              </button>
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                    <div
                      className="quote__file__body__info d-flex align-center justify-around text-center font-heavy"
                      style={{
                        flexDirection: files.length > 0 ? "row" : "column",
                      }}
                    >
                      <PublishOutlined
                        className="rotate-180 icon-light-grey"
                        fontSize="large"
                      />
                      {!isDragActive ? (
                        <span className="p-20">
                          Drag & drop file here or{" "}
                          <span className="quote__upload__browse">browse</span>
                        </span>
                      ) : (
                        <span className="p-20">Drop the files here...</span>
                      )}
                    </div>
                    <div
                      {...getRootProps({
                        onChange: (e) => {
                          setSelectedFile(e.target.files[0]);
                        },
                      })}
                      className="position-absolute t-0 w-100 h-100 bg-transparent"
                    >
                      <input id="quote__upload__input" {...getInputProps()} />
                    </div>
                  </div>
                  <div className="d-flex justify-end align-center py-10 px-20 border-top-1">
                    <button
                      className="quote__file__button px-40 border-0 outline-0 font-bold py-10 rounded"
                      onClick={closeUpload}
                    >
                      Done
                    </button>
                  </div>
                </div>
              </details>
            </div>
          </div>
        </div>
        <label className="d-flex flex-column">
          <p className="font-bold font-big mb-10 ml-10">Delivery date</p>
          <details id="quote__details__5" className="position-relative">
            <summary className="input d-flex align-center justify-between pointer list-none">
              <span className="quote__lang__to ellipsis">
                {urgent
                  ? "Deleivery in 48 hours"
                  : "Delivery in 5 working days"}
              </span>
              <EventOutlined />
            </summary>
            <div className="quote__drop__down d-flex flex-column position-absolute w-100 z-4 overflow-auto">
              <span
                className="quote__drop__lang d-flex justify-between p-20 border-bottom-1 text-capitalise pointer"
                style={{ color: urgent === true ? "#ff5e15" : "" }}
                onClick={(e) => {
                  updateUpload({ urgent: true });
                  setUrgent(true);
                  handleDropdownSelect("", "date");
                }}
              >
                Delivery in 48 hours
                {urgent === true && <Check />}
              </span>
              <span
                className="quote__drop__lang d-flex justify-between p-20 border-bottom-1 text-capitalise pointer"
                style={{ color: urgent === false ? "#ff5e15" : "" }}
                onClick={(e) => {
                  updateUpload({ urgent: false });
                  setUrgent(false);
                  handleDropdownSelect("", "date");
                }}
              >
                Delivery in 5 working days
                {urgent === false && <Check />}
              </span>
            </div>
          </details>
        </label>
        <div className="d-flex flex-column justify-end">
          <button
            className="border-0 outline-0 font-bold rounded p-10 font-normal quote__bottom__botton w-100"
            onClick={handleShowPrices}
            disabled={disable}
            style={{ opacity: disable ? "0.6" : "" }}
          >
            {showPrice ? "Hide Prices" : "Show Prices"}
          </button>
        </div>
      </div>
    </form>
  );
};

export default Quote;
